import React, { useState, useEffect } from 'react';
import { translations } from '../translations';
import { submitReview } from '../contexts/db.Reviews';
import '../styles/pages/Reviews.css';

function Reviews({ language }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Form state
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState('');
  const [hoverStars, setHoverStars] = useState(0);

  const t = translations[language];

  // Check if user already submitted
  useEffect(() => {
    const submitted = localStorage.getItem('reviewSubmitted');
    if (submitted) setHasSubmitted(true);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    // Validation
    if (!fullName.trim()) {
      setError(t.reviewFullNameRequired || 'Full name is required.');
      return;
    }
    if (!email.trim()) {
      setError(t.reviewEmailRequired || 'Email is required.');
      return;
    }
    if (stars === 0) {
      setError(t.reviewStarsRequired || 'Please select a rating.');
      return;
    }

    try {
      setSubmitting(true);
      await submitReview({
        full_name: fullName.trim(),
        user_identifier: email.trim(),
        stars,
        comment: comment.trim() || null,
      });

      setSuccessMessage(t.reviewSuccess || 'Thank you! Your review has been submitted successfully.');
      setHasSubmitted(true);
      localStorage.setItem('reviewSubmitted', email.trim());

      // Reset form
      setFullName('');
      setEmail('');
      setStars(0);
      setComment('');
    } catch (err) {
      if (err?.response?.status === 409) {
        setError(t.reviewAlreadySubmitted || 'You have already submitted a review.');
        setHasSubmitted(true);
        localStorage.setItem('reviewSubmitted', email.trim());
      } else {
        setError(t.reviewError || 'Failed to submit review. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="reviews-container">
      {/* Review Form Section */}
      <div className="review-form-section">
        <h2>{t.reviewFormTitle || 'Share Your Experience'}</h2>

        {successMessage && <div className="review-success-message">{successMessage}</div>}
        {error && <div className="review-error-message">{error}</div>}

        {!hasSubmitted ? (
          <form className="review-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="review-fullname">{t.reviewFullName || 'Full Name'}</label>
              <input
                id="review-fullname"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder={t.reviewFullName || 'Full Name'}
                maxLength={255}
              />
            </div>

            <div className="form-group">
              <label htmlFor="review-email">{t.reviewEmail || 'Email'}</label>
              <input
                id="review-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.reviewEmail || 'Email'}
                maxLength={255}
              />
            </div>

            <div className="form-group">
              <label>{t.reviewStars || 'Your Rating'}</label>
              <div className="star-selector">
                {Array.from({ length: 5 }, (_, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`star-btn ${i < (hoverStars || stars) ? 'active' : ''}`}
                    onClick={() => setStars(i + 1)}
                    onMouseEnter={() => setHoverStars(i + 1)}
                    onMouseLeave={() => setHoverStars(0)}
                  >
                    ★
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="review-comment">{t.reviewComment || 'Your Comment (optional)'}</label>
              <textarea
                id="review-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t.reviewComment || 'Your Comment (optional)'}
                rows={4}
                maxLength={1000}
              />
            </div>

            <button type="submit" className="review-submit-btn" disabled={submitting}>
              {submitting
                ? (t.reviewSubmitting || 'Submitting...')
                : (t.reviewSubmit || 'Submit Review')}
            </button>
          </form>
        ) : (
          <div className="review-already-submitted">
            <p>{t.reviewAlreadySubmitted || 'You have already submitted a review.'}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Reviews;
